import React, { useState, useRef } from "react";
import { useEffect } from "react";

export default function FileInput({
  isBulk = false,
  resetSignal = 0,
  labelSingle = "Fichier etgw",
  labelBulk = "Select folder",
  accept = ".etgw",
  uploadService,
  setImageUrl,
  onUploadComplete = () => {},
}) {
  const [file, setFile] = useState(null);
  const [files, setFiles] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [fileStatus, setFileStatus] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const cancelRef = useRef(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (resetSignal > 0) {
      clearInput();
    }
  }, [resetSignal]);

  const inputId = isBulk
    ? "hidden-file-input-bulk"
    : "hidden-file-input-single";

  /* ---------------- HANDLE INPUT ---------------- */
  const handleChange = (e) => {
    if (isBulk) {
      setFiles(e.target.files);
      setFileStatus([]);
    } else {
      setFile(e.target.files[0]);
    }
  };

  /* ---------------- UPLOAD IMAGE ---------------- */
  const uploadimg = async (file) => {
    const formData = new FormData();
    formData.append("file", file);

    if (!uploadService) {
      return file;
    }
    try {
      const res = await uploadService(formData, (e) =>
        setUploadProgress(Math.round((e.loaded * 100) / e.total))
      );

      setImageUrl(res.data.uploaded_file_url);
      return res; // return response
    } catch (err) {
      console.error("Error uploading image:", err);
      return null;
    }
  };

  /* ---------------- UPLOAD SINGLE FILE ---------------- */
  const uploadFile = async (fileToUpload, index) => {
    const formData = new FormData();
    formData.append("files", fileToUpload);

    if (!uploadService) {
      return file;
    }
    
    try {
      const res = await uploadService.upload(formData, uploadService.services_url_names.upload);

      const statusObj = {
        index,
        message: res.data?.success || res.data?.status||"Uploaded",
        response: res,
      };

      // update UI
      setFileStatus((prev) => [...prev, statusObj]);

      return statusObj; // return structured result
    } catch (err) {
      const errorObj = {
        index,
        message: `ERROR: ${err.message || err}`,
        response: null,
      };

      setFileStatus((prev) => [...prev, errorObj]);

      console.error("Upload error:", err);
      return errorObj;
    }
  };

  /* ---------------- UPLOAD HANDLER ---------------- */
  const uploadFiles = async () => {
  cancelRef.current = false; // reset cancel
  setIsUploading(true);

  const results = [];

  try {
    if (accept === "image/*" && file) {
      const res = await uploadimg(file);

      if (cancelRef.current) return;

      results.push({
        index: 1,
        message: "Image uploaded",
        response: res,
      });
    } 
    else if (isBulk && files) {
      for (let i = 0; i < files.length; i++) {
        if (cancelRef.current) return; // ✅ real-time cancel

        const result = await uploadFile(files[i], i + 1);

        if (cancelRef.current) return;

        results.push(result);
      }
    } 
    else if (!isBulk && file) {
      const result = await uploadFile(file, 1);
      results.push(result);
    }

    if (cancelRef.current) return;

    onUploadComplete(results);
    } catch (err) {
      console.error("Upload process failed:", err);
    } finally {
      setIsUploading(false);
    }
  };

  /* ---------------- CLEAR INPUT ---------------- */
  const clearInput = () => {
    cancelRef.current = true;
    if (inputRef.current) inputRef.current.value = "";
    setFile(null);
    setFiles(null);
    setFileStatus([]);
    setUploadProgress(0);

    setIsUploading(false);
  };

  /* ---------------- DISPLAY TEXT ---------------- */
  const displayText = isBulk
    ? files && files.length > 0
      ? `${files.length} file(s) selected`
      : labelBulk
    : file
    ? file.name
    : labelSingle;

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          padding: "8px 12px",
          borderBottom: "1px solid #ccc",
          cursor: "pointer",
          width: "300px",
        }}
      >
        {/* File input trigger */}
        <div onClick={() => inputRef.current.click()}>
          <button
            className="default-btn img_paper_clip"
            disabled={isUploading}
          >
            {/*isUploading ? "Uploading..." : ""*/}
          </button>

          <div
            style={{
              flex: 1,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {displayText}
          </div>

          <input
            ref={inputRef}
            id={inputId}
            type="file"
            style={{ display: "none" }}
            {...(isBulk
              ? { multiple: true, webkitdirectory: "true", directory: "true" }
              : {})}
            accept={accept}
            onChange={handleChange}
          />
        </div>

        {/* Buttons */}
        <button
          onClick={clearInput}
          className="label-btn"
          style={{ marginLeft: 8 }}
        >
          Clear
        </button>

        {isUploading ? (
          <div style={{ marginLeft: 8 }}>
            <div
              className="spinner"
              style={{ "--s-w": "16px", "--s-h": "16px" }}
            ></div>
          </div>
        ) : (
          <button
            className="default-btn img_upload"
            onClick={uploadFiles}
            style={{ marginLeft: 8 }}
          >
            {/*Upload*/}
          </button>
        )}
      </div>

      {/* Status log */}
      {fileStatus.length > 0 && (
        <ul style={{ marginTop: 8 }}>
          {fileStatus.map((status, i) => (
            <li key={i}>
              {status.index} - {status.message}
            </li>
          ))}
        </ul>
      )}

      {/* Progress */}
      {uploadProgress > 0 && accept === "image/*" && (
        <p>Upload Progress: {uploadProgress}%</p>
      )}
    </div>
  );
}